import { analyzeAndGroupCables, getCableDisplayName } from './cableLogic';

const generateId = (() => {
  let counter = 0;
  return (prefix) => `${prefix}-${counter++}`;
})();

const getFirstIndex = (cables = []) => {
  if (!cables.length) return Infinity;
  return Math.min(...cables.map(c => c.originalIndex ?? 0));
};

const buildSingleNode = (cable, level) => ({
  id: `single-${cable.originalIndex}`,
  level,
  type: 'single',
  cables: [cable],
  firstIndex: cable.originalIndex ?? 0,
  subGroups: []
});

const buildMultipleNode = (group, level, settings) => {
  const firstCable = group.cables[0];
  const label = getCableDisplayName(firstCable, settings?.lingua, settings);
  return {
    id: group.key || generateId('multiple'),
    level,
    type: 'multiple',
    label,
    cables: group.cables,
    firstIndex: getFirstIndex(group.cables),
    subGroups: group.cables.map(cable => buildSingleNode(cable, level + 1))
  };
};

const buildSheathNode = (sheathId, groupList, level, settings) => {
  const allCables = groupList.flatMap(group => group.cables);
  const subGroups = groupList.map(group =>
    group.isSingle
      ? buildSingleNode(group.cables[0], level + 1)
      : buildMultipleNode(group, level + 1, settings)
  );

  return {
    id: `sheath-${sheathId}-${level}-${generateId('sheath')}`,
    level,
    type: 'sheath',
    sheathId,
    label: `Guaina ${sheathId}`,
    cables: allCables,
    firstIndex: getFirstIndex(allCables),
    subGroups
  };
};

const buildSubGroups = (cables = [], level, settings, options = { allowSheath: true }) => {
  if (!cables || cables.length === 0) return [];

  const groups = analyzeAndGroupCables(cables, settings);
  const sheathBuckets = new Map();
  const regularGroups = [];

  groups.forEach(group => {
    const groupCables = group.cables || [];
    const sheathIds = Array.from(new Set(
      groupCables
        .filter(cable => Boolean(cable.guaina))
        .map(cable => cable.guaina)
    ));

    const canAttachToSheath =
      options.allowSheath &&
      sheathIds.length === 1;

    if (canAttachToSheath) {
      const sheathId = sheathIds[0];
      if (!sheathBuckets.has(sheathId)) {
        sheathBuckets.set(sheathId, []);
      }
      sheathBuckets.get(sheathId).push(group);
    } else {
      regularGroups.push(group);
    }
  });

  const resultNodes = [];

  sheathBuckets.forEach((groupList, sheathId) => {
    resultNodes.push(buildSheathNode(sheathId, groupList, level, settings));
  });

  regularGroups.forEach(group => {
    if (group.isSingle) {
      group.cables.forEach(cable => {
        resultNodes.push(buildSingleNode(cable, level));
      });
    } else {
      resultNodes.push(buildMultipleNode(group, level, settings));
    }
  });

  return resultNodes.sort((a, b) => (a.firstIndex ?? 0) - (b.firstIndex ?? 0));
};

const buildTopLevelNode = ({
  id,
  level,
  label,
  type = 'group',
  cables = [],
  settings,
  allowSheath = true,
  groupCode,
  groupKey,
  defaultTitle,
  subgroupIndex,
  totalSubgroups
}) => {
  if (type === 'single') {
    const cable = cables[0];
    return {
      ...buildSingleNode(cable, level),
      label: getCableDisplayName(cable, settings?.lingua, settings)
    };
  }

  const resolvedLabel = defaultTitle || label;

  return {
    id,
    level,
    type,
    label: resolvedLabel,
    defaultTitle: resolvedLabel,
    groupCode,
    groupKey,
    cables,
    firstIndex: getFirstIndex(cables),
    subGroups: buildSubGroups(cables, level + 1, settings, { allowSheath }),
    subgroupIndex: subgroupIndex ?? cables?.[0]?.subgroupIndex ?? null,
    totalSubgroups: totalSubgroups ?? cables?.[0]?.totalSubgroups ?? null
  };
};

export const buildNestedCableStructure = ({ groups = [], settings, forcedGroups = [], normalCables = [] }) => {
  if (groups.length > 0) {
    return groups.map(group => {
      if (group.isForcedGroup) {
        const groupName = group.groupData?.groupName || 'Forced Group';
        const subgroupIndex = group.groupData?.subgroupIndex || null;
        const totalSubgroups = group.groupData?.totalSubgroups || null;
        return buildTopLevelNode({
          id: group.key || groupName,
          level: 1,
          type: 'group',
          label: groupName,
          groupCode: groupName,
          groupKey: group.key || groupName,
          defaultTitle: groupName,
          cables: group.groupData?.allCables || [],
          settings,
          subgroupIndex,
          totalSubgroups
        });
      }

      const sortedGroup = group.sortedGroupData || { cables: group.cables || [] };
      const firstCable = sortedGroup.cables?.[0];
      const label = firstCable ? getCableDisplayName(firstCable, settings?.lingua, settings) : 'Group';
      const isSheath = sortedGroup.key && sortedGroup.key.startsWith('sheath-');
      const subgroupIndex = sortedGroup.cables?.[0]?.subgroupIndex || null;
      const totalSubgroups = sortedGroup.cables?.[0]?.totalSubgroups || null;
      const baseGroupCode = firstCable?.originalSiglatura || group.key;

      return buildTopLevelNode({
        id: group.key,
        level: 1,
        type: isSheath ? 'sheath' : 'group',
        label,
        cables: sortedGroup.cables || [],
        settings,
        allowSheath: !isSheath,
        groupCode: baseGroupCode,
        subgroupIndex,
        totalSubgroups
      });
    }).sort((a, b) => (a.firstIndex ?? 0) - (b.firstIndex ?? 0));
  }

  const nodes = [];

  forcedGroups.forEach(group => {
    if (group.isForcedGroup) {
      const groupName = group.groupData?.groupName || 'Forced Group';
      nodes.push(buildTopLevelNode({
        id: group.key || groupName,
        level: 1,
        type: 'group',
        label: groupName,
        groupCode: groupName,
        groupKey: group.key || groupName,
        defaultTitle: groupName,
        cables: group.groupData?.allCables || [],
        settings,
        subgroupIndex: group.groupData?.subgroupIndex || null,
        totalSubgroups: group.groupData?.totalSubgroups || null
      }));
    }
  });

  if (normalCables && normalCables.length > 0) {
    const groupMap = new Map();
    const ungroupedCables = [];

    normalCables.forEach(cable => {
      const groupCode = typeof cable.gruppo === 'string' ? cable.gruppo.trim() : '';
      if (groupCode) {
        if (!groupMap.has(groupCode)) {
          groupMap.set(groupCode, []);
        }
        groupMap.get(groupCode).push(cable);
      } else {
        ungroupedCables.push(cable);
      }
    });

    groupMap.forEach((groupCables, groupCode) => {
      const firstCable = groupCables?.[0];
      const baseGroupCode = firstCable?.originalSiglatura || groupCode;
      nodes.push(buildTopLevelNode({
        id: groupCode,
        level: 1,
        type: 'group',
        label: baseGroupCode,
        groupCode: baseGroupCode,
        groupKey: groupCode,
        defaultTitle: baseGroupCode,
        cables: groupCables,
        settings
      }));
    });

    if (ungroupedCables.length > 0) {
      const normalNodes = buildSubGroups(ungroupedCables, 1, settings, { allowSheath: true });
      nodes.push(...normalNodes);
    }
  }

  return nodes.sort((a, b) => (a.firstIndex ?? 0) - (b.firstIndex ?? 0));
};

